import { useEffect, useState } from 'react';

import { FieldValues, UseFormReturn } from 'react-hook-form';

import { SubmitDateRange } from '@/modules/common/components/templates/feature-details';
import { mapRateSourceFromDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { RateSource } from '@/modules/common/types/rate-source';
import {
  ChangeDateRange,
  ConflictSnapshot,
  PatchEntityEffect,
} from '@/modules/projects/types/project-conflicts';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';
import { formatDateToNonISOFormat } from '@/utils/date-formatters';
import { logger } from '@/utils/logger/logger';

import { ProjectFormData } from '../types';

interface UseConflictResolutionParams<TFormData extends FieldValues, TFieldKey extends string> {
  /**
   * Tag for logging purposes
   */
  tag: string;
  /**
   * Form instance from react-hook-form
   */
  form: UseFormReturn<TFormData>;
  /**
   * Current project ID (undefined in create mode)
   */
  projectId?: string;
  /**
   * Callback to check for conflicts
   * Returns conflict data with hasConflicts flag and items array
   */
  checkConflicts: (params: {
    projectId: string;
    effectiveStartDate: string;
    effectiveEndDate: string | null;
  }) => Promise<{
    hasConflicts: boolean;
    items: Array<{
      effectiveStartDate: string;
      effectiveEndDate: string | null;
      patchEffect: PatchEntityEffect;
      snapshot: any;
    }>;
  }>;
  /**
   * Whether conflict checking is in progress
   */
  isCheckingConflicts: boolean;
  /**
   * Callback to notify parent about form state changes
   */
  onFormStateChange?: (isDirty: boolean) => void;
  /**
   * Callback to get current form data with all fields
   */
  getFormData: () => ProjectFormData;
  /**
   * Callback to directly save data to API after conflict resolution
   */
  onDirectSave?: (
    data: ProjectFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
  /**
   * Whether we're in EditChange mode (editing an existing change)
   */
  isEditChangeMode?: boolean;
  /**
   * Original change date range (for EditChange mode)
   * Used to determine if the date range was extended
   */
  originalChangeRange?: ChangeDateRange;
}

interface UseConflictResolutionReturn<TFieldKey extends string> {
  /**
   * Whether to show the conflict resolution modal
   */
  showConflictModal: boolean;
  /**
   * Conflict snapshots to display in the modal
   */
  conflictSnapshots: ConflictSnapshot[];
  /**
   * Selected date range from the footer
   */
  selectedDateRange: { from: Date; to?: Date | null } | null;
  /**
   * Close the conflict modal
   */
  closeConflictModal: () => void;
  /**
   * Handle form submission with conflict detection
   * @param baseHandleSubmit - Base submit handler from useTabFormState
   * @param dateRange - Optional date range from footer
   */
  handleSubmitWithConflicts: (
    baseHandleSubmit: () => Promise<void>,
    dateRange?: SubmitDateRange
  ) => Promise<void>;
  /**
   * Handle conflict resolution by merging resolved values with current form data
   * @param resolvedValues - Resolved values from the conflict modal
   */
  handleResolveConflicts: (resolvedValues: Record<TFieldKey, any>) => Promise<void>;
}

/**
 * Reusable hook for handling conflict resolution in project detail tabs
 * Encapsulates conflict detection, modal state management, and resolution logic
 */
export function useProjectChangeConflictResolution<
  TFormData extends FieldValues,
  TFieldKey extends string,
>({
  tag,
  form,
  projectId,
  checkConflicts,
  isCheckingConflicts,
  onFormStateChange,
  getFormData,
  onDirectSave,
  isEditChangeMode = false,
  originalChangeRange,
}: UseConflictResolutionParams<TFormData, TFieldKey>): UseConflictResolutionReturn<TFieldKey> {
  // Conflict resolution state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictSnapshots, setConflictSnapshots] = useState<ConflictSnapshot[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    from: Date;
    to?: Date | null;
  } | null>(null);

  // Notify parent about conflict checking loading state changes.
  // Calling onFormStateChange(false) while isCheckingConflicts is true disables the save button
  // and triggers a page re-render so the parent can pick up isCheckingConflicts from the tab ref.
  useEffect(() => {
    if (isCheckingConflicts && onFormStateChange) {
      // Force a parent re-render while conflict check is in flight so the footer
      // reads the latest ref state and disables the submit button.
      onFormStateChange(false);
    }
  }, [isCheckingConflicts, onFormStateChange]);

  /**
   * Submit handler with conflict detection
   */
  const handleSubmitWithConflicts = async (
    baseHandleSubmit: () => Promise<void>,
    dateRange?: SubmitDateRange
  ): Promise<void> => {
    try {
      // Skip conflict detection if no project ID (create mode or missing data)
      if (!projectId) {
        await baseHandleSubmit();
        return;
      }

      // Skip conflict detection if no date range provided
      // This happens when only non-versioned fields changed
      if (!dateRange?.from) {
        await baseHandleSubmit();
        return;
      }

      const effectiveStartDate = formatDateToNonISOFormat(dateRange.from);
      const effectiveEndDate = dateRange.to ? formatDateToNonISOFormat(dateRange.to) : null;

      logger.debug(tag, 'Conflict detection check', {
        isEditChangeMode,
        hasOriginalChangeRange: !!originalChangeRange,
        effectiveStartDate,
        effectiveEndDate,
        originalStartDate: originalChangeRange?.startDate,
        originalEndDate: originalChangeRange?.endDate,
      });

      // In EditChange mode, skip conflict detection if date range wasn't extended
      // Only check for conflicts when:
      // 1. Start date moved EARLIER (extends range backwards), OR
      // 2. End date moved LATER (extends range forwards)
      if (isEditChangeMode && originalChangeRange) {
        // Normalize original dates to YYYY-MM-DD format for comparison
        // The dates from backend might include time component (e.g., "2025-12-12T00:00:00.000Z")
        const originalStartDate = originalChangeRange.startDate.split('T')[0];
        const originalEndDate = originalChangeRange.endDate
          ? originalChangeRange.endDate.split('T')[0]
          : null;

        // Check if start date moved earlier
        const startDateExtended = effectiveStartDate < originalStartDate;

        // Check if end date moved later
        // Handle null (indefinite) end dates: null means "infinity", so any specific date is earlier
        let endDateExtended = false;
        if (originalEndDate === null) {
          // Original is indefinite - can't extend further
          endDateExtended = false;
        } else if (effectiveEndDate === null) {
          // New is indefinite, original was specific - this extends the range
          endDateExtended = true;
        } else {
          // Both are specific dates - compare them
          endDateExtended = effectiveEndDate > originalEndDate;
        }

        // If dates weren't extended, skip conflict detection
        if (!startDateExtended && !endDateExtended) {
          logger.debug(
            tag,
            'EditChange mode: Date range not extended, skipping conflict detection',
            {
              originalStartDate,
              originalEndDate,
              newStartDate: effectiveStartDate,
              newEndDate: effectiveEndDate,
              startDateExtended,
              endDateExtended,
            }
          );

          // Save directly without checking conflicts
          if (onDirectSave && projectId) {
            const currentFormData = getFormData();
            await onDirectSave(
              {
                ...currentFormData,
                id: projectId,
              } as ProjectFormData,
              dateRange.from,
              dateRange.to
            );
            // Throw to prevent form manager from continuing with submission after direct save
            throw new Error('SAVE_COMPLETED');
          }

          await baseHandleSubmit();
          return;
        }

        logger.debug(tag, 'EditChange mode: Date range extended, checking for conflicts', {
          originalStartDate,
          originalEndDate,
          newStartDate: effectiveStartDate,
          newEndDate: effectiveEndDate,
          startDateExtended,
          endDateExtended,
        });
      }

      // Check for conflicts using the hook (handles loading state and error toasts)
      const conflictData = await checkConflicts({
        projectId,
        effectiveStartDate,
        effectiveEndDate,
      });

      // If conflicts exist, show the conflict resolution modal
      if (conflictData.hasConflicts) {
        const snapshots: ConflictSnapshot[] = conflictData.items.map(item => ({
          id: `${item.effectiveStartDate}-${item.effectiveEndDate || 'infinity'}`,
          effectiveStartDate: item.effectiveStartDate,
          effectiveEndDate: item.effectiveEndDate,
          patchEffect: item.patchEffect,
          data: item.snapshot,
        }));

        setConflictSnapshots(snapshots);
        setSelectedDateRange({ from: dateRange.from, to: dateRange.to });
        setShowConflictModal(true);

        // Throw to prevent the form manager from continuing with submission
        throw new Error('CONFLICT_RESOLUTION_REQUIRED');
      }

      // No conflicts, but we still need to save with the selected date range
      if (onDirectSave && projectId) {
        const currentFormData = getFormData();
        await onDirectSave(
          {
            ...currentFormData,
            id: projectId,
          } as ProjectFormData,
          dateRange.from,
          dateRange.to
        );
        // Throw to prevent form manager from continuing with submission after direct save
        throw new Error('SAVE_COMPLETED');
      }

      // Fallback to base submit if no onDirectSave callback
      await baseHandleSubmit();
    } catch (error) {
      // Re-throw special flow control errors without logging (expected flows)
      if (
        error instanceof Error &&
        (error.message === 'CONFLICT_RESOLUTION_REQUIRED' || error.message === 'SAVE_COMPLETED')
      ) {
        throw error;
      }

      // Restore footer interactivity after a failed conflict lookup.
      // The user still has unsaved changes and should be able to retry.
      onFormStateChange?.(true);

      // Other errors are already handled by the hook's onError callback
      logger.error(tag, 'Form submission error:', error);
      throw error;
    }
  };

  /**
   * Applies resolved userRates sub-row values back onto the current team members array.
   *
   * `getResolvedValues()` returns userRates as `[{ id, value: { rateSource: RateSourceDTO, rate } }]`
   * but the billing mapper expects `ProjectTeamMember[]` with `activeRateSource` set.
   * This function reconciles the two by updating each member's `activeRateSource` and the
   * corresponding rate field to match the resolved value selected in the conflict modal.
   */
  const applyResolvedUserRates = (
    resolvedItems: Array<{ id: string; value: { rateSource: RateSourceDTO; rate: number } | null }>,
    currentMembers: ProjectTeamMember[]
  ): ProjectTeamMember[] => {
    const resolvedMap = new Map(resolvedItems.map(item => [item.id, item.value]));

    return currentMembers
      .filter(member => {
        // If member was in the conflict modal and user selected "-" (null), exclude them
        if (resolvedMap.has(member.id) && resolvedMap.get(member.id) === null) {
          return false;
        }
        return true;
      })
      .map(member => {
        const resolved = resolvedMap.get(member.id);
        if (!resolved) return member;

        const activeRateSource = mapRateSourceFromDTO(resolved.rateSource);

        // Update the rate field that corresponds to the newly selected rate source
        const rateUpdate: Partial<ProjectTeamMember> = { activeRateSource };
        switch (activeRateSource) {
          case RateSource.UserExternalRate:
            rateUpdate.userExternalRateValue = resolved.rate;
            break;
          case RateSource.SeniorityBasedRate:
            rateUpdate.seniorityBasedRateValue = resolved.rate;
            break;
          case RateSource.RoleBasedRate:
            rateUpdate.roleBasedRateValue = resolved.rate;
            break;
          case RateSource.GlobalDefaultRate:
            rateUpdate.globalDefaultRateValue = resolved.rate;
            break;
          case RateSource.ProjectRateCard:
            rateUpdate.projectRateCardValue = resolved.rate;
            break;
          case RateSource.Custom:
            rateUpdate.customRateValue = resolved.rate;
            break;
        }

        return { ...member, ...rateUpdate };
      });
  };

  /**
   * Handle conflict resolution by merging resolved values with current form data and saving
   */
  const handleResolveConflicts = async (resolvedValues: Record<TFieldKey, any>): Promise<void> => {
    try {
      // Get the current form data (unchanged fields)
      const currentFormData = getFormData();

      // Transform resolved userRates from sub-row format back into ProjectTeamMember[] so the
      // billing mapper can read activeRateSource correctly. Without this step the mapper would
      // receive { id, value } objects instead of ProjectTeamMember objects and fall back to the
      // original rate sources.
      const resolvedUserRates = resolvedValues['userRates' as TFieldKey];
      if (Array.isArray(resolvedUserRates) && Array.isArray(currentFormData.selectedTeamMembers)) {
        resolvedValues = {
          ...resolvedValues,
          ['userRates' as TFieldKey]: undefined, // Remove from top-level resolved values
        };
        currentFormData.selectedTeamMembers = applyResolvedUserRates(
          resolvedUserRates,
          currentFormData.selectedTeamMembers
        );
      }

      // Merge current form data with resolved values from conflict modal
      // The resolved values take precedence for conflicting fields
      const mergedData = {
        ...currentFormData,
        ...resolvedValues,
      };

      // If onDirectSave callback is provided, use it to save directly to API
      if (onDirectSave && projectId && selectedDateRange) {
        await onDirectSave(
          {
            ...mergedData,
            id: projectId,
          } as ProjectFormData,
          selectedDateRange.from,
          selectedDateRange.to
        );

        // Update form with the saved values after successful save
        Object.entries(resolvedValues).forEach(([key, value]) => {
          form.setValue(key as any, value as any, { shouldValidate: false, shouldDirty: false });
        });

        // Close modal only after successful save
        setShowConflictModal(false);
      }
    } catch (error) {
      logger.error(tag, 'Error resolving conflicts:', error);
      // Restore footer interactivity so the user can close the modal and retry
      onFormStateChange?.(true);
      throw error;
    }
  };

  const closeConflictModal = () => {
    setShowConflictModal(false);
    // Restore dirty state after user cancels conflict resolution.
    // The form still has unsaved changes and the conflict check is no longer running.
    // This also triggers a page re-render so the footer reads isCheckingConflicts=false
    // and stops showing the loading spinner.
    if (onFormStateChange) {
      onFormStateChange(true);
    }
  };

  return {
    showConflictModal,
    conflictSnapshots,
    selectedDateRange,
    closeConflictModal,
    handleSubmitWithConflicts,
    handleResolveConflicts,
  };
}
